import { useEffect, useRef, useState } from "react";
import { api, extractError, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Palette, Save, Plus, Trash2, Home, Target, Trophy, Sparkles, Eye, Upload, ImageIcon, Video as VideoIcon, X, Link as LinkIcon, ArrowUp, ArrowDown, CalendarClock, Star, Search, Play, Scissors, Lock, GripVertical, Pencil,
  Activity, BookOpen, Brush, Music, Waves, Users, HeartHandshake, Briefcase, ClipboardCheck, Puzzle, Bus, MessageCircle, Award, GraduationCap } from "lucide-react";
import VideoTrimmerDialog from "@/components/VideoTrimmerDialog";
import IconPicker from "@/components/IconPicker";
import PosterCaptureDialog from "@/components/PosterCaptureDialog";

const ICON_CHOICES = [
  { name: "Activity", Icon: Activity },
  { name: "BookOpen", Icon: BookOpen },
  { name: "Brush", Icon: Brush },
  { name: "Home", Icon: Home },
  { name: "Music", Icon: Music },
  { name: "Waves", Icon: Waves },
  { name: "Users", Icon: Users },
  { name: "Trophy", Icon: Trophy },
  { name: "Target", Icon: Target },
  { name: "HeartHandshake", Icon: HeartHandshake },
  { name: "Sparkles", Icon: Sparkles },
  { name: "GraduationCap", Icon: GraduationCap },
  { name: "Briefcase", Icon: Briefcase },
  { name: "ClipboardCheck", Icon: ClipboardCheck },
  { name: "Palette", Icon: Palette },
  { name: "Puzzle", Icon: Puzzle },
  { name: "Bus", Icon: Bus },
  { name: "MessageCircle", Icon: MessageCircle },
  { name: "Award", Icon: Award },
];
import { toast } from "sonner";

const FIELD_GROUPS = [
  {
    id: "branding",
    title: "هوية الموقع (العنوان واسم المركز)",
    icon: Sparkles,
    color: "from-amber-500 to-orange-600",
    fields: [
      { key: "site_title", label: "عنوان الموقع (يظهر في تبويب المتصفح)", type: "text" },
      { key: "center_name", label: "اسم المركز (أعلى الصفحة الرئيسية والتذييل)", type: "text" },
    ],
  },
  {
    id: "hero",
    title: "قسم الترحيب (Hero)",
    icon: Sparkles,
    color: "from-sky-500 to-blue-600",
    fields: [
      { key: "hero_badge", label: "شارة (Badge) أعلى العنوان", type: "text" },
      { key: "hero_title_1", label: "السطر الأول من العنوان", type: "text" },
      { key: "hero_title_2", label: "السطر الثاني (بلون مميز)", type: "text" },
      { key: "hero_description", label: "الوصف التعريفي", type: "textarea", rows: 4 },
      { key: "hero_cta_text", label: "نص زر الدعوة الرئيسي", type: "text" },
    ],
  },
  {
    id: "vision",
    title: "الرؤية والرسالة",
    icon: Trophy,
    color: "from-emerald-500 to-teal-600",
    fields: [
      { key: "vision_title", label: "عنوان الرؤية", type: "text" },
      { key: "vision_body", label: "نص الرؤية", type: "textarea", rows: 4 },
      { key: "mission_title", label: "عنوان الرسالة", type: "text" },
      { key: "mission_body", label: "نص الرسالة", type: "textarea", rows: 4 },
    ],
  },
  {
    id: "cta",
    title: "قسم الدعوة السفلي (CTA)",
    icon: Target,
    color: "from-violet-500 to-purple-600",
    fields: [
      { key: "cta_title", label: "عنوان الدعوة", type: "text" },
      { key: "cta_body", label: "نص الدعوة", type: "textarea", rows: 3 },
      { key: "cta_button", label: "نص الزر", type: "text" },
    ],
  },
  {
    id: "contact",
    title: "معلومات التواصل (Footer)",
    icon: Home,
    color: "from-amber-500 to-orange-600",
    fields: [
      { key: "contact_phone", label: "الهاتف / نص تواصل معنا", type: "text" },
      { key: "contact_email", label: "البريد الإلكتروني", type: "text", dir: "ltr" },
    ],
  },
  {
    id: "director",
    title: "بطاقة مدير المركز",
    icon: Trophy,
    color: "from-indigo-500 to-blue-600",
    fields: [
      { key: "director_name", label: "اسم المدير", type: "text" },
      { key: "director_title", label: "المسمّى الوظيفي / اللقب", type: "text" },
      { key: "director_photo_url", label: "رابط الصورة الشخصية", type: "text", dir: "ltr" },
      { key: "director_bio", label: "نبذة تعريفية (جملتان)", type: "textarea", rows: 5 },
      { key: "director_whatsapp", label: "رقم واتساب المدير (مع رمز الدولة، بدون +)", type: "text", dir: "ltr" },
      { key: "director_whatsapp_message", label: "الرسالة المُعبَّأة تلقائياً عند الضغط", type: "textarea", rows: 3 },
    ],
  },
];

const PRESET_THEMES = [
  { name: "سماوي كلاسيكي", primary: "#0284c7", secondary: "#2563eb", accent: "#7c3aed" },
  { name: "زمردي طبيعي", primary: "#059669", secondary: "#0d9488", accent: "#0891b2" },
  { name: "بنفسجي ملكي", primary: "#7c3aed", secondary: "#a855f7", accent: "#ec4899" },
  { name: "برتقالي دافئ", primary: "#ea580c", secondary: "#f59e0b", accent: "#dc2626" },
  { name: "وردي مرح", primary: "#e11d48", secondary: "#ec4899", accent: "#8b5cf6" },
  { name: "نيلي عميق", primary: "#1e40af", secondary: "#4f46e5", accent: "#0ea5e9" },
];

export default function LandingSettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [newCertTitle, setNewCertTitle] = useState("");
  const [newCertCategory, setNewCertCategory] = useState("professional");
  const [newCertYear, setNewCertYear] = useState("");
  const [certSearch, setCertSearch] = useState("");
  const [certFilter, setCertFilter] = useState("all");
  const [uploadingDirVideo, setUploadingDirVideo] = useState(false);
  const [trimFile, setTrimFile] = useState(null);
  const [posterCaptureOpen, setPosterCaptureOpen] = useState(false);
  const [posterStats, setPosterStats] = useState(null);
  const [uploadingVariant, setUploadingVariant] = useState(null); // 'a' | 'b' | null
  const [unlocking, setUnlocking] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const dirVideoRef = useRef(null);
  const sigRef = useRef(null);
  const posterRef = useRef(null);
  const posterARef = useRef(null);
  const posterBRef = useRef(null);
  const collageRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const HERO_LABELS = ["الأنشطة الرياضية", "التعليم والقراءة", "الفن والإبداع", "مهارات يومية"];
  const HERO_ICONS_DEFAULT = ["Activity", "BookOpen", "Brush", "Home"];
  const fileRef = useRef(null);
  const certFileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/public/landing-settings");
      setForm({ ...data, objectives: data.objectives || [] });
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateObjective = (i, v) => {
    const arr = [...(form.objectives || [])];
    arr[i] = v;
    setField("objectives", arr);
  };
  const addObjective = () => setField("objectives", [...(form.objectives || []), "هدف جديد"]);
  const removeObjective = (i) => {
    const arr = [...(form.objectives || [])];
    arr.splice(i, 1);
    setField("objectives", arr);
  };

  const applyTheme = (t) => {
    setField("primary_color", t.primary);
    setField("secondary_color", t.secondary);
    setField("accent_color", t.accent);
  };

  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImg = f.type.startsWith("image/");
    const isVid = f.type.startsWith("video/");
    if (!isImg && !isVid) { toast.error("النوع غير مدعوم"); return; }
    if (isImg && f.size > 8 * 1024 * 1024) { toast.error("الصورة تتجاوز 8MB"); return; }
    if (isVid && f.size > 30 * 1024 * 1024) { toast.error("الفيديو يتجاوز 30MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/landing-hero-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, hero_media_type: data.hero_media_type, hero_media_path: data.hero_media_path }));
      toast.success("تم رفع وسائط Hero بنجاح");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearHero = async () => {
    if (!confirm("إزالة وسائط Hero الحالية والعودة إلى الشعار الافتراضي؟")) return;
    try {
      await api.delete("/admin/landing-hero");
      setForm((s) => ({ ...s, hero_media_type: "url", hero_media_path: "" }));
      toast.success("تم الحذف");
    } catch (e) { toast.error(extractError(e)); }
  };

  const uploadCert = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("الملف يتجاوز 8MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", newCertTitle || "شهادة");
    fd.append("category", newCertCategory);
    if (newCertYear) fd.append("issue_year", String(newCertYear));
    setUploadingCert(true);
    try {
      await api.post("/admin/landing-cert-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تمت إضافة الشهادة");
      setNewCertTitle("");
      setNewCertYear("");
      await load();
    } catch (err) { toast.error(extractError(err)); }
    finally {
      setUploadingCert(false);
      if (certFileRef.current) certFileRef.current.value = "";
    }
  };

  const updateCertCategory = async (cert, category) => {
    try {
      await api.patch(`/admin/landing-cert/${cert.id}`, { category });
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const updateCertYear = async (cert, year) => {
    const y = parseInt(year, 10);
    if (!y || y < 1970 || y > 2100) { toast.error("سنة غير صحيحة"); return; }
    try {
      await api.patch(`/admin/landing-cert/${cert.id}`, { issue_year: y });
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const moveCert = async (cert, direction) => {
    try {
      await api.post(`/admin/landing-cert/${cert.id}/move`, { direction });
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const sortCertsByYear = async () => {
    try {
      await api.post("/admin/landing-certs/sort-by-year");
      toast.success("تم الترتيب حسب السنة (الأحدث أولاً)");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleCertFeature = async (cert) => {
    try {
      await api.post(`/admin/landing-cert/${cert.id}/feature-toggle`);
      toast.success(cert.featured ? "أُلغي التمييز" : "تم التمييز — ستظهر أولاً");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const performDirectorVideoUpload = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    setUploadingDirVideo(true);
    try {
      await api.post("/admin/director-video-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع فيديو الترحيب");
      await load();
    } catch (err) { toast.error(extractError(err)); }
    finally {
      setUploadingDirVideo(false);
      if (dirVideoRef.current) dirVideoRef.current.value = "";
    }
  };

  const uploadDirectorVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Compute duration
    let dur = 0;
    try {
      dur = await new Promise((resolve) => {
        const url = URL.createObjectURL(f);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration || 0); };
        v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
        v.src = url;
      });
    } catch { /* noop */ }
    const bigSize = f.size > 20 * 1024 * 1024;
    const longDur = dur > 90;
    if (bigSize || longDur) {
      const msg = longDur
        ? `مدة الفيديو ${Math.round(dur)} ثانية — يُنصح باقتصاصه وضغطه.`
        : `حجم الفيديو ${(f.size/1024/1024).toFixed(1)}MB — يُنصح باقتصاصه وضغطه.`;
      const doTrim = confirm(`${msg}\n\nهل تريد فتح أداة الاقتصاص والضغط داخل المتصفح؟ (اضغط "إلغاء" للرفع كما هو)`);
      if (doTrim) {
        setTrimFile(f);
        if (dirVideoRef.current) dirVideoRef.current.value = "";
        return;
      }
    }
    if (f.size > 60 * 1024 * 1024) { toast.error("الفيديو يتجاوز 60MB — يجب اقتصاصه/ضغطه"); if (dirVideoRef.current) dirVideoRef.current.value = ""; return; }
    await performDirectorVideoUpload(f);
  };

  const clearDirectorVideo = async () => {
    if (!confirm("حذف فيديو الترحيب؟")) return;
    try {
      await api.delete("/admin/director-video");
      await load();
      toast.success("تم الحذف");
    } catch (e) { toast.error(extractError(e)); }
  };

  const uploadPoster = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("الحجم يتجاوز 8MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/admin/director-video-poster-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع صورة الغلاف");
      await load();
    } catch (err) { toast.error(extractError(err)); }
    if (posterRef.current) posterRef.current.value = "";
  };

  const clearPoster = async () => {
    try { await api.delete("/admin/director-video-poster"); await load(); toast.success("تم الحذف"); }
    catch (e) { toast.error(extractError(e)); }
  };

  const loadPosterStats = async () => {
    try {
      const { data } = await api.get("/admin/director-poster-stats");
      setPosterStats(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadPosterStats(); }, []);

  const uploadPosterVariant = async (slot, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("الحجم يتجاوز 8MB"); return; }
    const fd = new FormData();
    fd.append("slot", slot);
    fd.append("file", file);
    setUploadingVariant(slot);
    try {
      await api.post("/admin/director-poster-variant", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`تم رفع البوستر ${slot.toUpperCase()}`);
      await Promise.all([load(), loadPosterStats()]);
    } catch (e) { toast.error(extractError(e)); }
    finally {
      setUploadingVariant(null);
      if (slot === "a" && posterARef.current) posterARef.current.value = "";
      if (slot === "b" && posterBRef.current) posterBRef.current.value = "";
    }
  };

  const clearPosterVariant = async (slot) => {
    if (!confirm(`حذف البوستر ${slot.toUpperCase()}؟`)) return;
    try {
      await api.delete(`/admin/director-poster-variant/${slot}`);
      await Promise.all([load(), loadPosterStats()]);
      toast.success("تم الحذف");
    } catch (e) { toast.error(extractError(e)); }
  };

  const promotePosterVariant = async (slot) => {
    if (!confirm(`اعتماد البوستر ${slot.toUpperCase()} كغلاف رئيسي وإنهاء الاختبار؟`)) return;
    try {
      await api.post("/admin/director-poster-promote", { slot });
      toast.success("تم اعتماد الفائز");
      await Promise.all([load(), loadPosterStats()]);
    } catch (e) { toast.error(extractError(e)); }
  };

  const setActiveVariant = async (variant) => {
    try {
      await api.patch("/admin/active-poster-variant", { variant });
      setForm((f) => ({ ...f, active_poster_variant: variant }));
      toast.success(variant === "auto" ? "وضع تلقائي 50/50" : `عرض ${variant.toUpperCase()} فقط`);
      await loadPosterStats();
    } catch (e) { toast.error(extractError(e)); }
  };

  const unlockWinner = async () => {
    if (!confirm("إلغاء تثبيت البوستر الفائز الآن؟ يمكنك بعدها بدء اختبار A/B جديد فوراً.")) return;
    setUnlocking(true);
    try {
      await api.post("/admin/director-poster-unlock");
      toast.success("تم إلغاء التثبيت — يمكنك رفع بوسترات جديدة");
      await loadPosterStats();
    } catch (e) { toast.error(extractError(e)); }
    finally { setUnlocking(false); }
  };

  const uploadSignature = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("الحجم يتجاوز 2MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingSig(true);
    try {
      await api.post("/admin/director-signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع التوقيع — سيظهر تلقائياً على شهادات الإنجاز");
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploadingSig(false); if (sigRef.current) sigRef.current.value = ""; }
  };

  const clearSignature = async () => {
    if (!confirm("حذف توقيع المدير؟ ستعود شهادات الإنجاز لعرض سطر التوقيع الفارغ.")) return;
    try {
      await api.delete("/admin/director-signature");
      toast.success("تم الحذف");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const uploadCollageSlot = async (slot, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("الصورة تتجاوز 8MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("slot", String(slot));
    try {
      await api.post("/admin/landing-collage-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم الرفع");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const clearCollageSlot = async (slot) => {
    try { await api.delete(`/admin/landing-collage/${slot}`); await load(); toast.success("تم الحذف"); }
    catch (e) { toast.error(extractError(e)); }
  };

  const moveFeaturedCert = async (cert, direction) => {
    try { await api.post(`/admin/landing-cert/${cert.id}/move-featured`, { direction }); await load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const deleteCert = async (cert) => {
    if (!confirm(`حذف الشهادة "${cert.title}"؟`)) return;
    try {
      await api.delete(`/admin/landing-cert/${cert.id}`);
      toast.success("تم الحذف");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/landing-settings", form);
      toast.success("تم حفظ إعدادات الصفحة الرئيسية بنجاح");
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="p-12 text-center text-slate-400" data-testid="landing-settings-loading">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6" data-testid="landing-settings-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <Palette className="w-8 h-8 text-violet-600" />
            تخصيص الصفحة الرئيسية
          </h1>
          <p className="text-slate-500 mt-2">حرّر النصوص والألوان والصور التي تظهر لزوار الموقع</p>
        </div>
        <div className="flex gap-2">
          <a href="/welcome" target="_blank" rel="noreferrer">
            <Button variant="outline" className="h-12 gap-2" data-testid="preview-landing-btn">
              <Eye className="w-4 h-4" /> معاينة
            </Button>
          </a>
          <Button
            onClick={save}
            disabled={saving}
            className="h-12 gap-2 bg-gradient-to-l from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700"
            data-testid="save-landing-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ..." : "حفظ التغييرات"}
          </Button>
        </div>
      </div>

      {/* Colors */}
      <Card className="border-2 border-violet-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-sky-500" />
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-violet-600 text-white flex items-center justify-center">
              <Palette className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">الألوان الأساسية</h2>
              <p className="text-sm text-slate-500">استخدم Color Picker أو اختر ثيماً جاهزاً</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <ColorPicker label="اللون الأساسي (Primary)" value={form.primary_color} onChange={(v) => setField("primary_color", v)} testid="color-primary" />
            <ColorPicker label="اللون الثانوي (Secondary)" value={form.secondary_color} onChange={(v) => setField("secondary_color", v)} testid="color-secondary" />
            <ColorPicker label="لون التمييز (Accent)" value={form.accent_color} onChange={(v) => setField("accent_color", v)} testid="color-accent" />
          </div>

          <div>
            <Label className="font-bold mb-2 block">ثيمات جاهزة</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {PRESET_THEMES.map((t) => (
                <button
                  key={t.name}
                  onClick={() => applyTheme(t)}
                  className="group p-3 rounded-2xl border-2 border-slate-200 hover:border-slate-400 transition-all text-right"
                  data-testid={`preset-${t.name}`}
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-6 h-6 rounded-lg" style={{ backgroundColor: t.primary }} />
                    <div className="w-6 h-6 rounded-lg" style={{ backgroundColor: t.secondary }} />
                    <div className="w-6 h-6 rounded-lg" style={{ backgroundColor: t.accent }} />
                  </div>
                  <div className="text-xs font-bold text-slate-700">{t.name}</div>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hero Media (image or looping video) */}
      <Card className="border-2 border-sky-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-sky-500 via-blue-500 to-indigo-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center">
              <ImageIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">وسائط الـ Hero</h2>
              <p className="text-sm text-slate-500">صورة أو فيديو قصير (looping) يظهر في أعلى الصفحة الرئيسية</p>
            </div>
          </div>

          {form.hero_media_path ? (
            <div className="relative rounded-2xl overflow-hidden border-2 border-slate-200 bg-slate-100">
              {form.hero_media_type === "video" ? (
                <video src={`${API}/public/landing-hero`} controls muted className="w-full max-h-72 object-contain bg-black" data-testid="hero-media-preview-video" />
              ) : (
                <img src={`${API}/public/landing-hero`} alt="hero" className="w-full max-h-72 object-contain" data-testid="hero-media-preview-image" />
              )}
              <div className="absolute top-2 left-2 flex gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/80 text-white text-[10px] font-black">
                  {form.hero_media_type === "video" ? <><VideoIcon className="w-3 h-3" /> فيديو</> : <><ImageIcon className="w-3 h-3" /> صورة</>}
                </span>
                <Button type="button" size="sm" onClick={clearHero} className="h-7 gap-1 bg-rose-600 hover:bg-rose-700 text-white" data-testid="clear-hero-btn">
                  <X className="w-3 h-3" /> إزالة
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center bg-slate-50">
              <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <div className="font-bold text-slate-700">لا توجد وسائط مرفوعة</div>
              <div className="text-xs text-slate-500 mt-1">سيُستخدم شعار المركز الافتراضي، أو الرابط في الحقل أدناه.</div>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
            onChange={uploadHero}
            className="hidden"
            data-testid="hero-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="gap-2 bg-gradient-to-l from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700"
              data-testid="upload-hero-btn"
            >
              <Upload className="w-4 h-4" /> {uploading ? "جاري الرفع..." : (form.hero_media_path ? "استبدال بملف جديد" : "رفع صورة أو فيديو")}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 space-y-1">
            <div>• الصور: jpg / png / webp / gif — حتى 8MB</div>
            <div>• الفيديو: mp4 / webm / mov — حتى 30MB (يُشغَّل تلقائياً بلا صوت، يتكرر)</div>
          </div>

          <div className="pt-4 border-t border-slate-100 space-y-1.5">
            <Label className="font-bold inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> أو استخدم رابط خارجي (Fallback عند عدم وجود ملف مرفوع)</Label>
            <Input
              value={form.hero_image_url || ""}
              onChange={(e) => setField("hero_image_url", e.target.value)}
              dir="ltr"
              placeholder="https://example.com/hero.jpg"
              data-testid="field-hero_image_url"
            />
          </div>
        </CardContent>
      </Card>

      {/* Director Welcome Video */}
      <Card className="border-2 border-emerald-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center">
              <VideoIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">فيديو ترحيب المدير (60 ثانية)</h2>
              <p className="text-sm text-slate-500">يظهر كزر تشغيل داخل بطاقة Hero — يفتح في نافذة كاملة</p>
            </div>
          </div>

          {form.director_video_path ? (
            <div className="rounded-2xl overflow-hidden border-2 border-slate-200 bg-black">
              <video src={`${API}/public/director-video`} controls className="w-full max-h-80" data-testid="director-video-preview" />
              <div className="p-3 bg-white flex items-center justify-between border-t border-slate-100">
                <div className="inline-flex items-center gap-1 text-xs font-black text-emerald-700">
                  <Play className="w-3 h-3" /> فيديو الترحيب مفعّل
                </div>
                <Button size="sm" onClick={clearDirectorVideo} className="gap-1 bg-rose-600 hover:bg-rose-700 h-8" data-testid="clear-director-video-btn">
                  <X className="w-3 h-3" /> حذف
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center bg-slate-50">
              <VideoIcon className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <div className="font-bold text-slate-700">لا يوجد فيديو ترحيب</div>
              <div className="text-xs text-slate-500 mt-1">ارفع فيديو ~60 ثانية يوضّح رؤية المركز.</div>
            </div>
          )}

          <input
            ref={dirVideoRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={uploadDirectorVideo}
            className="hidden"
            data-testid="director-video-input"
          />
          <Button
            type="button"
            onClick={() => dirVideoRef.current?.click()}
            disabled={uploadingDirVideo}
            className="gap-2 bg-gradient-to-l from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            data-testid="upload-director-video-btn"
          >
            <Upload className="w-4 h-4" /> {uploadingDirVideo ? "جاري الرفع..." : (form.director_video_path ? "استبدال بفيديو جديد" : "رفع فيديو ترحيب")}
          </Button>
          <div className="text-[11px] text-slate-500">mp4 / webm / mov — حتى 60MB (يفضّل 60 ثانية)</div>
        </CardContent>
      </Card>

      {/* Director Welcome Video */}
      <Card className="border-2 border-emerald-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center">
              <VideoIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">فيديو ترحيب المدير (60 ثانية)</h2>
              <p className="text-sm text-slate-500">يظهر كزر تشغيل داخل بطاقة Hero — يفتح في نافذة كاملة</p>
            </div>
          </div>

          {form.director_video_path ? (
            <div className="rounded-2xl overflow-hidden border-2 border-slate-200 bg-black">
              <video
                src={`${API}/public/director-video`}
                controls
                poster={form.director_video_poster_path ? `${API}/public/director-video-poster` : undefined}
                className="w-full max-h-80"
                data-testid="director-video-preview"
              />
              <div className="p-3 bg-white flex items-center justify-between border-t border-slate-100">
                <div className="inline-flex items-center gap-1 text-xs font-black text-emerald-700">
                  <Play className="w-3 h-3" /> فيديو الترحيب مفعّل
                </div>
                <Button size="sm" onClick={clearDirectorVideo} className="gap-1 bg-rose-600 hover:bg-rose-700 h-8" data-testid="clear-director-video-btn">
                  <X className="w-3 h-3" /> حذف
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center bg-slate-50">
              <VideoIcon className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <div className="font-bold text-slate-700">لا يوجد فيديو ترحيب</div>
              <div className="text-xs text-slate-500 mt-1">ارفع فيديو ~60 ثانية يوضّح رؤية المركز.</div>
            </div>
          )}

          <input ref={dirVideoRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={uploadDirectorVideo} className="hidden" data-testid="director-video-input" />
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => dirVideoRef.current?.click()} disabled={uploadingDirVideo} className="gap-2 bg-gradient-to-l from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" data-testid="upload-director-video-btn">
              <Upload className="w-4 h-4" /> {uploadingDirVideo ? "جاري الرفع..." : (form.director_video_path ? "استبدال بفيديو جديد" : "رفع فيديو ترحيب")}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            <Scissors className="w-3 h-3" /> إذا تجاوز 20MB أو 90 ثانية، سيُقترح اقتصاصه/ضغطه داخل المتصفح تلقائياً
          </div>
          <VideoTrimmerDialog
            open={!!trimFile}
            file={trimFile}
            onCancel={() => setTrimFile(null)}
            onReady={async (f, posterFile) => {
              setTrimFile(null);
              await performDirectorVideoUpload(f);
              if (posterFile) {
                try {
                  const fd = new FormData();
                  fd.append("file", posterFile);
                  await api.post("/admin/director-video-poster-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  await load();
                  toast.success("تم استخراج غلاف تلقائي من الفيديو");
                } catch { /* poster is nice-to-have */ }
              }
            }}
          />

          {/* Poster frame */}
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <div className="text-sm font-black text-slate-700 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> صورة الغلاف (Poster) — قبل التشغيل
            </div>
            {form.director_video_poster_path ? (
              <div className="flex items-center gap-3 flex-wrap">
                <img src={`${API}/public/director-video-poster`} alt="poster" className="w-32 aspect-video object-cover rounded-xl border border-slate-200" data-testid="poster-preview" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" type="button" onClick={() => posterRef.current?.click()} variant="outline" className="gap-1"><Upload className="w-3 h-3" /> استبدال</Button>
                  {form.director_video_path && (
                    <Button size="sm" type="button" onClick={() => setPosterCaptureOpen(true)} variant="outline" className="gap-1 text-emerald-700 border-emerald-300" data-testid="regen-poster-btn">
                      <Sparkles className="w-3 h-3" /> استخراج غلاف جديد
                    </Button>
                  )}
                  <Button size="sm" type="button" onClick={clearPoster} className="gap-1 bg-rose-600 hover:bg-rose-700" data-testid="clear-poster-btn"><X className="w-3 h-3" /> حذف</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => posterRef.current?.click()} variant="outline" className="gap-2" data-testid="upload-poster-btn">
                  <Upload className="w-4 h-4" /> رفع صورة غلاف
                </Button>
                {form.director_video_path && (
                  <Button type="button" onClick={() => setPosterCaptureOpen(true)} variant="outline" className="gap-2 text-emerald-700 border-emerald-300" data-testid="regen-poster-btn">
                    <Sparkles className="w-4 h-4" /> استخراج غلاف من الفيديو
                  </Button>
                )}
              </div>
            )}
            <input ref={posterRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPoster} className="hidden" data-testid="poster-input" />
            <div className="text-[11px] text-slate-500">صورة (jpg/png/webp) — حتى 8MB. تُعرض قبل تشغيل الفيديو.</div>
            <PosterCaptureDialog open={posterCaptureOpen} onClose={() => setPosterCaptureOpen(false)} onDone={load} />
          </div>
        </CardContent>
      </Card>

      {/* Poster A/B Testing */}
      <Card className="border-2 border-emerald-100 overflow-hidden" data-testid="signature-card">
        <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center">
              <Award className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <h2 className="text-xl font-black text-slate-900">توقيع المدير على شهادات الإنجاز</h2>
              <p className="text-sm text-slate-500">ارفع صورة PNG بخلفية شفافة (يفضّل) — ستظهر على كل شهادة "شهادة إنجاز" تُصدر للأطفال.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(`${API}/admin/mastery-certificate-preview`, "_blank")}
              className="gap-2 text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="cert-preview-btn"
            >
              <Eye className="w-4 h-4" /> معاينة الشهادة
            </Button>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-56 h-24 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
              {form.director_signature_path ? (
                <img src={`${API}/public/director-signature`} alt="توقيع المدير" className="max-h-full max-w-full object-contain" data-testid="signature-preview" />
              ) : (
                <div className="text-slate-400 text-xs">لا يوجد توقيع بعد</div>
              )}
            </div>
            <input
              ref={sigRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => uploadSignature(e.target.files?.[0])}
              className="hidden"
              data-testid="signature-input"
            />
            <Button
              type="button"
              onClick={() => sigRef.current?.click()}
              disabled={uploadingSig}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="upload-signature-btn"
            >
              <Upload className="w-4 h-4" /> {uploadingSig ? "جارٍ الرفع..." : (form.director_signature_path ? "استبدال التوقيع" : "رفع التوقيع")}
            </Button>
            {form.director_signature_path && (
              <Button type="button" variant="outline" onClick={clearSignature} className="gap-2 text-rose-600 border-rose-200" data-testid="clear-signature-btn">
                <X className="w-4 h-4" /> حذف
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Poster A/B Testing */}
      <Card className="border-2 border-fuchsia-100 overflow-hidden" data-testid="poster-ab-card">
        <div className="h-1 bg-gradient-to-r from-fuchsia-500 via-pink-500 to-rose-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-600 text-white flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <h2 className="text-xl font-black text-slate-900">اختبار A/B لصورة الغلاف</h2>
              <p className="text-sm text-slate-500">ارفع نسختين للغلاف — سيُعرض كل زائر إحداهما ونحسب نسب التشغيل.</p>
            </div>
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              {[
                { key: "auto", label: "تلقائي 50/50" },
                { key: "a", label: "A فقط" },
                { key: "b", label: "B فقط" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setActiveVariant(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${form.active_poster_variant === opt.key ? "bg-white shadow text-fuchsia-700" : "text-slate-500 hover:text-slate-800"}`}
                  data-testid={`active-variant-${opt.key}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {!form.director_video_path && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 font-bold">
              ارفع فيديو الترحيب أولاً حتى تتمكن من تجربة البوسترات.
            </div>
          )}

          {posterStats?.winner_locked_until && new Date(posterStats.winner_locked_until) > new Date() && (
            <div className="p-4 rounded-2xl bg-gradient-to-l from-amber-50 via-white to-yellow-50 border-2 border-amber-300 flex items-center justify-between gap-3 flex-wrap" data-testid="winner-lock-banner">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 text-white flex items-center justify-center shadow-lg">
                  <Lock className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-black text-amber-900 text-lg">
                    🔒 البوستر الفائز مثبّت لمدة {Math.ceil((new Date(posterStats.winner_locked_until) - new Date()) / (1000*60*60*24))} يوم متبقٍ
                  </div>
                  <div className="text-xs text-amber-800 font-bold mt-0.5">
                    تم تثبيت النسخة <b>{posterStats.winner_locked_slot?.toUpperCase()}</b> بعد فوزها لضمان استمرار الأداء الجيد. لا يمكن بدء اختبار A/B جديد قبل انتهاء المدة أو الإلغاء اليدوي.
                  </div>
                  <div className="text-[10px] text-amber-700 mt-1">
                    ينتهي التثبيت: {new Date(posterStats.winner_locked_until).toLocaleDateString("ar", { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                </div>
              </div>
              <Button
                onClick={unlockWinner}
                disabled={unlocking}
                variant="outline"
                className="gap-2 border-amber-400 text-amber-800 hover:bg-amber-100"
                data-testid="unlock-winner-btn"
              >
                <X className="w-4 h-4" /> {unlocking ? "جارٍ..." : "إلغاء التثبيت الآن"}
              </Button>
            </div>
          )}

          {posterStats?.winner && posterStats?.has_a && posterStats?.has_b && (
            <div className="p-4 rounded-2xl bg-gradient-to-l from-emerald-50 via-white to-teal-50 border-2 border-emerald-300 flex items-center justify-between gap-3 flex-wrap animate-pulse-slow" data-testid="ab-auto-promote-banner">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-black text-emerald-900 text-lg">
                    🎉 اقتراح: بوستر {posterStats.winner.toUpperCase()} هو الفائز الواضح!
                  </div>
                  <div className="text-xs text-emerald-700 font-bold mt-0.5">
                    نتيجة {posterStats.winner === "a" ? posterStats.a_pct : posterStats.b_pct}% مقابل {posterStats.winner === "a" ? posterStats.b_pct : posterStats.a_pct}% من أصل {posterStats.total} تشغيل — اعتماده الآن يوقف الاختبار ويخصّص التجربة للجميع.
                  </div>
                </div>
              </div>
              <Button
                onClick={() => promotePosterVariant(posterStats.winner)}
                className="gap-2 bg-gradient-to-l from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black shadow-lg h-11"
                data-testid="auto-promote-winner-btn"
              >
                <Trophy className="w-4 h-4" /> اعتمد الفائز الآن
              </Button>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {["a", "b"].map((slot) => {
              const path = slot === "a" ? form.director_poster_a_path : form.director_poster_b_path;
              const plays = slot === "a" ? (posterStats?.a_plays ?? 0) : (posterStats?.b_plays ?? 0);
              const pct = slot === "a" ? (posterStats?.a_pct ?? 0) : (posterStats?.b_pct ?? 0);
              const isWinner = posterStats?.winner === slot;
              return (
                <div key={slot} className={`rounded-2xl border-2 p-4 space-y-3 ${isWinner ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`poster-variant-${slot}`}>
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-white ${slot === "a" ? "bg-fuchsia-600" : "bg-sky-600"}`}>
                        {slot.toUpperCase()}
                      </div>
                      <div className="font-black text-slate-800">بوستر {slot.toUpperCase()}</div>
                    </div>
                    {isWinner && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-black">
                        <Trophy className="w-3 h-3" /> الفائز
                      </span>
                    )}
                  </div>

                  {path ? (
                    <img src={`${API}/public/director-poster-variant/${slot}?t=${plays}`} alt={`Poster ${slot}`} className="w-full aspect-video object-cover rounded-xl border border-slate-200" data-testid={`poster-${slot}-preview`} />
                  ) : (
                    <div className="w-full aspect-video rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400 text-sm">
                      لم يتم الرفع بعد
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="rounded-lg bg-slate-50 p-2">
                      <div className="text-[10px] text-slate-500 font-bold">مرات التشغيل</div>
                      <div className="text-lg font-black text-slate-900" data-testid={`poster-${slot}-plays`}>{plays}</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-2">
                      <div className="text-[10px] text-slate-500 font-bold">النسبة</div>
                      <div className="text-lg font-black text-slate-900">{pct}%</div>
                    </div>
                  </div>

                  <input
                    ref={slot === "a" ? posterARef : posterBRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => uploadPosterVariant(slot, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`poster-${slot}-input`}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => (slot === "a" ? posterARef : posterBRef).current?.click()}
                      disabled={uploadingVariant === slot}
                      className={`gap-1 ${slot === "a" ? "bg-fuchsia-600 hover:bg-fuchsia-700" : "bg-sky-600 hover:bg-sky-700"}`}
                      data-testid={`poster-${slot}-upload-btn`}
                    >
                      <Upload className="w-3.5 h-3.5" /> {uploadingVariant === slot ? "جارٍ..." : (path ? "استبدال" : "رفع")}
                    </Button>
                    {path && (
                      <>
                        <Button type="button" size="sm" variant="outline" onClick={() => promotePosterVariant(slot)} className="gap-1 text-emerald-700 border-emerald-300" data-testid={`poster-${slot}-promote`}>
                          <Trophy className="w-3.5 h-3.5" /> اعتماد
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => clearPosterVariant(slot)} className="gap-1 text-rose-600 border-rose-200" data-testid={`poster-${slot}-clear`}>
                          <X className="w-3.5 h-3.5" /> حذف
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap text-xs">
            <div className="text-slate-600">
              إجمالي التشغيلات: <span className="font-black text-slate-900" data-testid="poster-total-plays">{posterStats?.total ?? 0}</span>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={loadPosterStats} className="gap-1" data-testid="poster-refresh-stats">
              <Eye className="w-3.5 h-3.5" /> تحديث الإحصائيات
            </Button>
          </div>
        </CardContent>
      </Card>


      {/* Hero Collage — 4 custom photos */}
      <Card className="border-2 border-pink-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-pink-500 via-rose-500 to-red-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 text-white flex items-center justify-center">
              <ImageIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">صور معرض الـ Hero (2×2)</h2>
              <p className="text-sm text-slate-500">ارفع 4 صور حقيقية من المركز — تظهر تحت الشعار على `/welcome`</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="collage-grid">
            {[0, 1, 2, 3].map((i) => {
              const path = (form.hero_collage_paths || [])[i];
              const label = (form.hero_collage_labels || [])[i] || HERO_LABELS[i];
              return (
                <div key={i} className="space-y-1.5" data-testid={`collage-slot-${i}`}>
                  <div className="text-xs font-black text-slate-500">{i + 1}. مسمّى الفتحة</div>
                  {(() => {
                    const iconName = (form.hero_collage_icons || HERO_ICONS_DEFAULT)[i] || "Activity";
                    const IconMeta = ICON_CHOICES.find((c) => c.name === iconName) || ICON_CHOICES[0];
                    const Icon = IconMeta.Icon;
                    return (
                      <div className="flex items-center gap-1.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shrink-0 shadow-sm" title={iconName} data-testid={`collage-icon-preview-${i}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <Input
                          value={label}
                          onChange={(e) => {
                            const arr = [...(form.hero_collage_labels || HERO_LABELS)];
                            while (arr.length < 4) arr.push("");
                            arr[i] = e.target.value;
                            setField("hero_collage_labels", arr);
                          }}
                          className="h-8 text-xs font-bold flex-1"
                          data-testid={`collage-label-${i}`}
                        />
                      </div>
                    );
                  })()}
                  <IconPicker
                    value={(form.hero_collage_icons || HERO_ICONS_DEFAULT)[i] || "Activity"}
                    onChange={(name) => {
                      const arr = [...(form.hero_collage_icons || HERO_ICONS_DEFAULT)];
                      while (arr.length < 4) arr.push("Activity");
                      arr[i] = name;
                      setField("hero_collage_icons", arr);
                    }}
                    testid={`collage-icon-picker-${i}`}
                  />
                  <div className="relative aspect-square rounded-2xl overflow-hidden border-2 border-slate-200 bg-slate-100">
                    {path ? (
                      <img src={`${API}/public/landing-collage/${i}`} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                        <ImageIcon className="w-8 h-8 mb-1" />
                        <div className="text-[10px] font-bold">افتراضي (Unsplash)</div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={collageRefs[i]}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => uploadCollageSlot(i, e.target.files?.[0])}
                    className="hidden"
                    data-testid={`collage-input-${i}`}
                  />
                  <div className="flex gap-1">
                    <Button size="sm" type="button" onClick={() => collageRefs[i].current?.click()} variant="outline" className="flex-1 gap-1 h-8 text-xs" data-testid={`upload-collage-${i}`}>
                      <Upload className="w-3 h-3" /> {path ? "استبدال" : "رفع"}
                    </Button>
                    {path && (
                      <Button size="sm" type="button" onClick={() => clearCollageSlot(i)} className="gap-1 h-8 text-xs bg-rose-600 hover:bg-rose-700" data-testid={`clear-collage-${i}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-500">اترك أي فتحة فارغة لتُستخدم الصورة الافتراضية. غيّر النصوص أعلى كل فتحة ثم اضغط "حفظ التغييرات" بأعلى الصفحة.</div>
        </CardContent>
      </Card>

      {/* External Contributions (director) */}
      <Card className="border-2 border-violet-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900">إسهامات المدير الخارجية</h2>
              <p className="text-sm text-slate-500">الدورات التدريبية وورش العمل التي أعطاها المدير خارج المركز — تظهر في نافذة عند النقر على زر "الإسهامات الخارجية" في الصفحة الرئيسية</p>
            </div>
          </div>
          <ContributionsManager />
        </CardContent>
      </Card>

      {/* Certifications */}
      <Card className="border-2 border-amber-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">شهادات المدير (Gallery)</h2>
                <p className="text-sm text-slate-500">تُعرض بشكل مصغّر داخل بطاقة المدير — بالنقر تفتح بحجم كامل</p>
              </div>
            </div>
            {(form.certifications || []).length > 1 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  onClick={sortCertsByYear}
                  variant="outline"
                  className="gap-2"
                  data-testid="sort-certs-year-btn"
                >
                  <CalendarClock className="w-4 h-4" /> ترتيب حسب السنة (الأحدث أولاً)
                </Button>
                <div className="flex items-center gap-1 border rounded-lg bg-amber-50 border-amber-200 px-2 h-10">
                  <Star className="w-3.5 h-3.5 text-amber-600 fill-amber-500" />
                  <span className="text-xs font-black text-amber-700">حد المميّزة</span>
                  <select
                    value={form.featured_cert_max || 3}
                    onChange={(e) => setField("featured_cert_max", parseInt(e.target.value, 10))}
                    className="bg-transparent text-xs font-black text-amber-800 outline-none"
                    data-testid="featured-cert-max-select"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          {(form.certifications || []).length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-6 text-center bg-slate-50 text-slate-500 text-sm">
              لا توجد شهادات مرفوعة بعد
            </div>
          ) : (
            <>
              {/* Search + filter (visible when > 3 certs) */}
              {(form.certifications || []).length > 3 && (
                <div className="grid md:grid-cols-[1fr_auto] gap-2 mb-3" data-testid="cert-search-panel">
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={certSearch}
                      onChange={(e) => setCertSearch(e.target.value)}
                      placeholder="بحث في الشهادات بالعنوان..."
                      className="pr-10"
                      data-testid="cert-search-input"
                    />
                  </div>
                  <div className="flex gap-1">
                    {[
                      { id: "all", label: "الكل" },
                      { id: "master", label: "🎓 ماجستير" },
                      { id: "diploma", label: "📜 دبلومات" },
                      { id: "professional", label: "⭐ مهنية" },
                    ].map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setCertFilter(f.id)}
                        className={`px-3 py-2 rounded-lg text-xs font-black transition-all ${certFilter === f.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`cert-filter-${f.id}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const q = certSearch.trim().toLowerCase();
                const filtered = (form.certifications || []).filter((c) => {
                  if (certFilter !== "all" && (c.category || "professional") !== certFilter) return false;
                  if (q && !((c.title || "").toLowerCase().includes(q))) return false;
                  return true;
                });
                if (filtered.length === 0) {
                  return <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-2xl">لا نتائج مطابقة</div>;
                }
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="certs-grid">
                    {filtered.map((c) => {
                const CAT_META = {
                  master: { label: "ماجستير", cls: "bg-violet-100 text-violet-700 border-violet-300" },
                  diploma: { label: "دبلومات", cls: "bg-sky-100 text-sky-700 border-sky-300" },
                  professional: { label: "شهادات مهنية", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
                };
                const cat = c.category || "professional";
                const meta = CAT_META[cat];
                return (
                  <div key={c.id} className={`relative group rounded-2xl overflow-hidden border-2 shadow-sm ${c.featured ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`} data-testid={`cert-item-${c.id}`}>
                    <div className="aspect-[3/4] bg-slate-100">
                      {(c.mime || "").startsWith("image/") ? (
                        <img src={`${API}/public/landing-cert/${c.id}`} alt={c.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                          <Trophy className="w-10 h-10 mb-1" />
                          <span className="text-xs font-black">PDF</span>
                        </div>
                      )}
                      {c.featured && (
                        <div className="absolute top-2 right-2 rounded-full px-2 py-0.5 bg-amber-500 text-white text-[9px] font-black inline-flex items-center gap-0.5 shadow" data-testid={`cert-featured-${c.id}`}>
                          <Star className="w-2.5 h-2.5 fill-white" /> مميّزة
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-white border-t border-slate-100 space-y-1.5">
                      <div className="text-xs font-bold text-slate-800 line-clamp-2 leading-tight">{c.title}</div>
                      <select
                        value={cat}
                        onChange={(e) => updateCertCategory(c, e.target.value)}
                        className={`w-full text-[10px] font-black rounded-lg px-2 py-1 border ${meta.cls}`}
                        data-testid={`cert-category-${c.id}`}
                      >
                        <option value="master">ماجستير</option>
                        <option value="diploma">دبلومات</option>
                        <option value="professional">شهادات مهنية</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="1970"
                          max="2100"
                          placeholder="سنة"
                          defaultValue={c.issue_year || ""}
                          onBlur={(e) => {
                            const v = e.target.value;
                            if (v && parseInt(v, 10) !== c.issue_year) updateCertYear(c, v);
                          }}
                          className="h-7 text-[11px] font-bold flex-1"
                          data-testid={`cert-year-${c.id}`}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          onClick={() => toggleCertFeature(c)}
                          className={`h-7 w-7 shrink-0 ${c.featured ? "bg-amber-50 text-amber-700 border-amber-300" : ""}`}
                          title={c.featured ? "إلغاء التمييز" : "تمييز (max 3)"}
                          data-testid={`cert-star-${c.id}`}
                        >
                          <Star className={`w-3 h-3 ${c.featured ? "fill-amber-500" : ""}`} />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          onClick={() => (c.featured ? moveFeaturedCert(c, "up") : moveCert(c, "up"))}
                          className={`h-7 w-7 shrink-0 ${c.featured ? "text-amber-700" : ""}`}
                          title={c.featured ? "نقل للأعلى ضمن المميّزة" : "نقل للأعلى"}
                          data-testid={`cert-move-up-${c.id}`}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          onClick={() => (c.featured ? moveFeaturedCert(c, "down") : moveCert(c, "down"))}
                          className={`h-7 w-7 shrink-0 ${c.featured ? "text-amber-700" : ""}`}
                          title={c.featured ? "نقل للأسفل ضمن المميّزة" : "نقل للأسفل"}
                          data-testid={`cert-move-down-${c.id}`}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      onClick={() => deleteCert(c)}
                      className="absolute top-2 left-2 h-7 w-7 bg-rose-600 hover:bg-rose-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-cert-${c.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
                    })}
                  </div>
                );
              })()}
            </>
          )}

          <input
            ref={certFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={uploadCert}
            className="hidden"
            data-testid="cert-file-input"
          />
          <div className="grid md:grid-cols-[1fr_140px_100px_auto] gap-2 items-center">
            <Input
              value={newCertTitle}
              onChange={(e) => setNewCertTitle(e.target.value)}
              placeholder="عنوان الشهادة (مثال: ماجستير التربية الخاصة)"
              data-testid="cert-title-input"
            />
            <select
              value={newCertCategory}
              onChange={(e) => setNewCertCategory(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700"
              data-testid="cert-category-select"
            >
              <option value="master">🎓 ماجستير</option>
              <option value="diploma">📜 دبلومات</option>
              <option value="professional">⭐ شهادات مهنية</option>
            </select>
            <Input
              type="number"
              min="1970"
              max="2100"
              value={newCertYear}
              onChange={(e) => setNewCertYear(e.target.value)}
              placeholder="السنة"
              data-testid="cert-year-input"
            />
            <Button
              type="button"
              onClick={() => certFileRef.current?.click()}
              disabled={uploadingCert}
              className="gap-2 bg-gradient-to-l from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
              data-testid="upload-cert-btn"
            >
              <Upload className="w-4 h-4" /> {uploadingCert ? "جاري الرفع..." : "رفع شهادة"}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500">صور (jpg / png / webp) أو ملف PDF — حتى 8MB لكل ملف. اختر التصنيف والسنة (اختياري) قبل الرفع.</div>
        </CardContent>
      </Card>

      {/* Text field groups */}
      {FIELD_GROUPS.map((g) => (
        <Card key={g.id} className="border border-slate-200 overflow-hidden">
          <div className={`h-1 bg-gradient-to-r ${g.color}`} />
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${g.color} text-white flex items-center justify-center`}>
                <g.icon className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-black text-slate-900">{g.title}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {g.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2 space-y-1.5" : "space-y-1.5"}>
                  <Label className="font-bold">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      value={form[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      rows={f.rows || 3}
                      data-testid={`field-${f.key}`}
                    />
                  ) : (
                    <Input
                      value={form[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      dir={f.dir || "auto"}
                      data-testid={`field-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Objectives */}
      <Card className="border border-slate-200 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-rose-500 to-pink-600" />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 text-white flex items-center justify-center">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">قائمة الأهداف</h2>
                <p className="text-sm text-slate-500">حتى 20 هدف — تظهر مرقمة على الصفحة الرئيسية</p>
              </div>
            </div>
            <Button onClick={addObjective} variant="outline" className="gap-2" data-testid="add-objective-btn">
              <Plus className="w-4 h-4" /> إضافة
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label className="font-bold">عنوان القسم</Label>
            <Input
              value={form.objectives_title || ""}
              onChange={(e) => setField("objectives_title", e.target.value)}
              data-testid="field-objectives_title"
            />
          </div>

          <div className="space-y-2">
            {(form.objectives || []).map((obj, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`objective-row-${i}`}>
                <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white font-black flex items-center justify-center text-sm">
                  {i + 1}
                </div>
                <Input
                  value={obj}
                  onChange={(e) => updateObjective(i, e.target.value)}
                  data-testid={`objective-input-${i}`}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => removeObjective(i)}
                  className="text-rose-600 border-rose-200 shrink-0"
                  data-testid={`delete-objective-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {(!form.objectives || form.objectives.length === 0) && (
              <div className="text-center text-slate-400 py-6">لا توجد أهداف — أضف أولها</div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="h-14 px-8 gap-2 bg-gradient-to-l from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 shadow-2xl"
          data-testid="save-landing-btn-bottom"
        >
          <Save className="w-5 h-5" /> {saving ? "جارٍ الحفظ..." : "حفظ كل التغييرات"}
        </Button>
      </div>
    </div>
  );
}

function ColorPicker({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-bold">{label}</Label>
      <div className="flex items-center gap-2">
        <div
          className="w-14 h-11 rounded-xl border-2 border-slate-200 shrink-0 shadow-inner"
          style={{ backgroundColor: value || "#000" }}
        />
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-14 h-11 rounded-xl cursor-pointer border border-slate-200"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          dir="ltr"
          className="flex-1 font-mono text-sm"
          placeholder="#0284c7"
          data-testid={`${testid}-input`}
        />
      </div>
    </div>
  );
}

function ContributionsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", kind: "course", year: "", event_date: "", org: "", location: "", url: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [mediaFor, setMediaFor] = useState(null);
  const [editingContrib, setEditingContrib] = useState(null);
  const KINDS = { course: "📚 دورة", workshop: "🛠️ ورشة", lecture: "🎤 محاضرة", talk: "🗣️ حديث", seminar: "🎓 ندوة" };
  const SECTIONS = [
    { id: "seminar", label: "قسم الندوات", emoji: "🎓" },
    { id: "workshop", label: "قسم الورش", emoji: "🛠️" },
    { id: "course", label: "قسم الدورات", emoji: "📚" },
    { id: "lecture", label: "قسم المحاضرات", emoji: "🎤" },
    { id: "talk", label: "قسم الأحاديث", emoji: "🗣️" },
  ];

  const load = () => {
    setLoading(true);
    api.get("/public/director-contributions").then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("العنوان مطلوب"); return; }
    setSaving(true);
    try {
      await api.post("/admin/director-contributions", { ...form, year: form.year ? Number(form.year) : null });
      toast.success("تمت الإضافة");
      setForm({ title: "", kind: "course", year: "", event_date: "", org: "", location: "", url: "", description: "" });
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذه الإسهامة؟")) return;
    try {
      await api.delete(`/admin/director-contributions/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="contributions-manager">
      <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/30 p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">عنوان الإسهامة *</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="مثال: دورة ABA للمعلمين" data-testid="contrib-title" />
        </div>
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">النوع</Label>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="contrib-kind">
            {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">السنة</Label>
          <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2024" data-testid="contrib-year" />
        </div>
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">تاريخ الفعالية (اختياري)</Label>
          <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="contrib-event-date" />
        </div>
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">الجهة المنظّمة</Label>
          <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="جمعية / مؤسسة" data-testid="contrib-org" />
        </div>
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">المكان</Label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="الكويت — قاعة X" data-testid="contrib-location" />
        </div>
        <div>
          <Label className="text-xs font-black text-violet-800 mb-1 block">رابط (اختياري)</Label>
          <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." dir="ltr" data-testid="contrib-url" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-black text-violet-800 mb-1 block">وصف مختصر (اختياري)</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="مثال: قدّم المدير ندوة تدريبية بمشاركة معلمات الفصول الخاصة..." rows={3} data-testid="contrib-description" />
        </div>
        <div className="md:col-span-2">
          <Button onClick={submit} disabled={saving} className="gap-2 bg-gradient-to-l from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700" data-testid="contrib-add-btn">
            <Plus className="w-4 h-4" /> {saving ? "جارٍ الإضافة..." : "إضافة إسهامة"}
          </Button>
        </div>
      </div>

      <div>
        <div className="text-xs font-black text-slate-600 mb-2">القائمة الحالية ({items.length})</div>
        {loading ? (
          <div className="text-center py-6 text-slate-400 text-sm">جارٍ التحميل...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-2xl">لم تُضَف إسهامات بعد</div>
        ) : (
          <div className="space-y-6" data-testid="contribs-list">
            {SECTIONS.map((sec) => {
              const group = items.filter((c) => c.kind === sec.id);
              if (group.length === 0) return null;
              return (
                <div key={sec.id} data-testid={`contribs-section-${sec.id}`}>
                  <div className="flex items-center gap-2 mb-2 pb-1.5 border-b-2 border-violet-100">
                    <div className="text-lg">{sec.emoji}</div>
                    <div className="font-black text-sm text-violet-800">{sec.label}</div>
                    <div className="text-[10px] font-bold text-violet-500 bg-violet-50 px-2 py-0.5 rounded-full">
                      {group.length}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {group.map((c) => (
                      <div key={c.id} className="p-3 rounded-xl border-2 border-violet-100 bg-white flex items-start justify-between gap-2 hover:border-violet-300 transition" data-testid={`contrib-item-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">{KINDS[c.kind] || c.kind}</span>
                            {c.year && <span className="text-[10px] font-black text-slate-500">{c.year}</span>}
                            {(c.media || []).length > 0 && (
                              <span className="text-[10px] font-black text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 px-2 py-0.5 rounded-full">
                                📎 {c.media.length} وسيط
                              </span>
                            )}
                          </div>
                          <div className="font-bold text-sm text-slate-900 truncate">{c.title}</div>
                          {(c.org || c.location) && <div className="text-[11px] text-slate-500 truncate">{c.org}{c.org && c.location ? " · " : ""}{c.location}</div>}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => setEditingContrib(c)} className="text-emerald-700 border-emerald-200 gap-1 text-[10px] font-black px-2" data-testid={`contrib-edit-${c.id}`} title="تعديل">
                            <Pencil className="w-3 h-3" /> تعديل
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setMediaFor(c)} className="text-violet-700 border-violet-200 gap-1" data-testid={`contrib-media-${c.id}`} title="إدارة الوسائط">
                            <ImageIcon className="w-3.5 h-3.5" /> <VideoIcon className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => remove(c.id)} className="text-rose-600 border-rose-200 gap-1 text-[10px] font-black px-2" data-testid={`contrib-delete-${c.id}`} title="حذف">
                            <Trash2 className="w-3 h-3" /> حذف
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {mediaFor && (
        <ContributionMediaDialog
          contribution={mediaFor}
          onClose={() => { setMediaFor(null); load(); }}
        />
      )}
      {editingContrib && (
        <EditContributionDialog
          contribution={editingContrib}
          KINDS={KINDS}
          onCancel={() => setEditingContrib(null)}
          onSaved={() => { setEditingContrib(null); load(); }}
        />
      )}
    </div>
  );
}


function EditContributionDialog({ contribution: c, KINDS, onCancel, onSaved }) {
  const [form, setForm] = useState({
    title: c.title || "",
    kind: c.kind || "course",
    year: c.year || "",
    event_date: c.event_date || "",
    org: c.org || "",
    location: c.location || "",
    url: c.url || "",
    description: c.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [testimonials, setTestimonials] = useState(c.testimonials || []);
  const [tForm, setTForm] = useState({ name: "", role: "", quote: "" });
  const [tBusy, setTBusy] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { toast.error("العنوان مطلوب"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/director-contributions/${c.id}`, {
        ...form,
        year: form.year ? Number(form.year) : null,
      });
      toast.success("تم حفظ التعديلات");
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const addTestimonial = async () => {
    if (!tForm.name.trim() || !tForm.quote.trim()) { toast.error("الاسم والاقتباس مطلوبان"); return; }
    setTBusy(true);
    try {
      const r = await api.post(`/admin/director-contributions/${c.id}/testimonials`, tForm);
      setTestimonials((prev) => [...prev, r.data]);
      setTForm({ name: "", role: "", quote: "" });
      toast.success("تمت إضافة الاقتباس");
    } catch (e) { toast.error(extractError(e)); }
    finally { setTBusy(false); }
  };

  const deleteTestimonial = async (tid) => {
    if (!window.confirm("حذف هذا الاقتباس؟")) return;
    try {
      await api.delete(`/admin/director-contributions/${c.id}/testimonials/${tid}`);
      setTestimonials((prev) => prev.filter((x) => x.id !== tid));
      toast.success("تم الحذف");
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="contrib-edit-dialog"
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div className="sticky top-0 bg-gradient-to-l from-emerald-600 to-sky-600 text-white px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div>
            <div className="text-xs font-black opacity-90">تعديل إسهامة</div>
            <div className="font-black text-lg truncate max-w-md">{c.title}</div>
          </div>
          <button onClick={onCancel} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="contrib-edit-close" title="إغلاق">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs font-black text-emerald-800 mb-1 block">عنوان الإسهامة *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contrib-edit-title" />
          </div>
          <div>
            <Label className="text-xs font-black text-emerald-800 mb-1 block">النوع</Label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm" data-testid="contrib-edit-kind">
              {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-black text-emerald-800 mb-1 block">السنة</Label>
            <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="contrib-edit-year" />
          </div>
          <div>
            <Label className="text-xs font-black text-emerald-800 mb-1 block">تاريخ الفعالية (اختياري)</Label>
            <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="contrib-edit-event-date" />
          </div>
          <div>
            <Label className="text-xs font-black text-emerald-800 mb-1 block">الجهة المنظّمة</Label>
            <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} data-testid="contrib-edit-org" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-black text-emerald-800 mb-1 block">الموقع</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="contrib-edit-location" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-black text-emerald-800 mb-1 block">رابط إضافي (اختياري)</Label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" data-testid="contrib-edit-url" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-black text-emerald-800 mb-1 block">الوصف</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} maxLength={1500} data-testid="contrib-edit-description" />
            <div className="text-[10px] text-slate-400 mt-1 text-left">{(form.description || "").length}/1500</div>
          </div>
          <div className="md:col-span-2 mt-4 pt-4 border-t-2 border-dashed border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">💬</span>
              <Label className="text-sm font-black text-amber-800">شهادات المشاركين ({testimonials.length})</Label>
            </div>
            <div className="rounded-xl border-2 border-amber-100 bg-amber-50/60 p-3 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input placeholder="الاسم *" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="testimonial-name" />
                <Input placeholder="الصفة (مديرة، معلمة...)" value={tForm.role} onChange={(e) => setTForm({ ...tForm, role: e.target.value })} data-testid="testimonial-role" />
              </div>
              <Textarea placeholder="الاقتباس *" value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} rows={2} maxLength={400} data-testid="testimonial-quote" />
              <Button onClick={addTestimonial} disabled={tBusy} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="testimonial-add">
                <Plus className="w-3.5 h-3.5" /> {tBusy ? "..." : "إضافة اقتباس"}
              </Button>
            </div>
            {testimonials.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="testimonials-list">
                {testimonials.map((t) => (
                  <div key={t.id} className="rounded-xl bg-white border border-amber-200 p-3 flex items-start gap-2" data-testid={`testimonial-item-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-700 italic">"{t.quote}"</p>
                      <div className="text-[10px] font-black text-amber-900 mt-1">— {t.name}{t.role && <span className="text-slate-500 font-bold"> · {t.role}</span>}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => deleteTestimonial(t.id)} className="text-rose-600 border-rose-200 px-2" data-testid={`testimonial-delete-${t.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-2 rounded-b-3xl">
          <Button variant="outline" onClick={onCancel} className="gap-1.5" data-testid="contrib-edit-cancel">
            <X className="w-4 h-4" /> إلغاء
          </Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="contrib-edit-save">
            <Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ..." : "حفظ التعديلات"}
          </Button>
        </div>
      </div>
    </div>
  );
}


function ContributionMediaDialog({ contribution, onClose }) {
  const [items, setItems] = useState(contribution.media || []);
  const [coverId, setCoverId] = useState(contribution.cover_media_id || "");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/public/director-contributions");
      const fresh = (r.data || []).find((c) => c.id === contribution.id);
      setItems(fresh?.media || []);
      setCoverId(fresh?.cover_media_id || "");
    } catch { /* silent */ }
  };

  const pinCover = async (mid) => {
    try {
      await api.patch(`/admin/director-contributions/${contribution.id}/cover-media/${mid}`);
      toast.success("تم تثبيت هذه الصورة كغلاف");
      setCoverId(mid);
    } catch (e) { toast.error(extractError(e)); }
  };
  const unpinCover = async () => {
    try {
      await api.delete(`/admin/director-contributions/${contribution.id}/cover-media`);
      toast.success("تم إلغاء تثبيت الغلاف");
      setCoverId("");
    } catch (e) { toast.error(extractError(e)); }
  };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const fd = new FormData();
        fd.append("file", f);
        fd.append("caption", "");
        setProgress(Math.round(((i) / files.length) * 100));
        await api.post(`/admin/director-contributions/${contribution.id}/media`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`تم رفع ${files.length} ملف بنجاح`);
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (mid) => {
    if (!window.confirm("حذف هذا الوسيط؟")) return;
    try {
      await api.delete(`/admin/director-contributions/${contribution.id}/media/${mid}`);
      toast.success("تم الحذف");
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveCaption = async (mid, caption) => {
    try {
      await api.patch(`/admin/director-contributions/${contribution.id}/media/${mid}`, { caption });
      toast.success("تم حفظ التعليق");
    } catch (e) { toast.error(extractError(e)); }
  };

  // Drag-and-drop reorder
  const dragIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const onDragStart = (idx) => (e) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* ignore */ }
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragIndex.current !== null && dragIndex.current !== idx) setDragOverIndex(idx);
  };
  const onDragLeave = () => setDragOverIndex(null);
  const onDrop = (idx) => async (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    setDragOverIndex(null);
    if (from === null || from === idx) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setItems(next); // optimistic
    try {
      await api.patch(`/admin/director-contributions/${contribution.id}/media-order`, {
        order: next.map((m) => m.id),
      });
      toast.success("تم إعادة الترتيب");
    } catch (err) {
      toast.error(extractError(err));
      await refresh();
    }
  };

  const move = (idx, dir) => async () => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      await api.patch(`/admin/director-contributions/${contribution.id}/media-order`, {
        order: next.map((m) => m.id),
      });
    } catch (err) {
      toast.error(extractError(err));
      await refresh();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="contrib-media-dialog"
    >
      <div
        className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div className="sticky top-0 bg-gradient-to-l from-violet-600 to-fuchsia-600 text-white px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <div className="text-xs font-black opacity-90">إدارة الوسائط</div>
            <div className="font-black text-lg truncate max-w-lg">{contribution.title}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={onClose} data-testid="contrib-media-close">
            <X className="w-4 h-4" /> إغلاق
          </Button>
        </div>
        <div className="p-6 space-y-4">
          <div
            className="border-2 border-dashed border-violet-300 bg-violet-50/40 rounded-2xl p-6 text-center hover:bg-violet-50 transition cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files || [])); }}
            data-testid="contrib-media-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-violet-500 mb-2" />
            <div className="font-black text-slate-900 text-sm">اسحب وأفلت الصور/الفيديوهات هنا أو اضغط للاختيار</div>
            <div className="text-xs text-slate-500 mt-1">صور: jpg/png/webp (حد 12MB) · فيديو: mp4/webm/mov (حد 200MB)</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
              multiple
              className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))}
              data-testid="contrib-media-file-input"
            />
            {uploading && (
              <div className="mt-3">
                <div className="text-xs font-bold text-violet-700">جارٍ الرفع... {progress}%</div>
                <div className="w-full h-1.5 bg-violet-100 rounded-full mt-1 overflow-hidden">
                  <div className="h-full bg-gradient-to-l from-violet-500 to-fuchsia-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-2xl">لم تُرفع وسائط بعد</div>
          ) : (
            <div>
              <div className="text-xs font-black text-slate-600 mb-2 flex items-center gap-2">
                <span>الوسائط المرفوعة ({items.length})</span>
                {items.length > 1 && <span className="text-[10px] font-bold text-violet-500">💡 اسحب البطاقات لإعادة الترتيب</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contrib-media-grid">
                {items.map((m, idx) => (
                  <div
                    key={m.id}
                    draggable
                    onDragStart={onDragStart(idx)}
                    onDragOver={onDragOver(idx)}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop(idx)}
                    className={`transition ${dragOverIndex === idx ? "ring-4 ring-violet-400 rounded-2xl" : ""}`}
                    data-testid={`contrib-media-drag-${m.id}`}
                  >
                    <MediaItemEditor
                      media={m}
                      contributionId={contribution.id}
                      index={idx}
                      total={items.length}
                      isCover={coverId === m.id}
                      onDelete={() => remove(m.id)}
                      onSaveCaption={(caption) => saveCaption(m.id, caption)}
                      onMoveUp={move(idx, -1)}
                      onMoveDown={move(idx, +1)}
                      onPinCover={() => pinCover(m.id)}
                      onUnpinCover={unpinCover}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function MediaItemEditor({ media: m, contributionId, index, total, isCover, onDelete, onSaveCaption, onMoveUp, onMoveDown, onPinCover, onUnpinCover }) {
  const [caption, setCaption] = useState(m.caption || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterVersion, setPosterVersion] = useState(0);

  const save = async () => {
    setSaving(true);
    try { await onSaveCaption(caption); setDirty(false); } finally { setSaving(false); }
  };

  return (
    <div className={`relative rounded-2xl overflow-hidden border-2 cursor-move ${isCover ? "border-amber-400 ring-2 ring-amber-300 bg-amber-50" : "border-slate-200 bg-slate-100"}`} data-testid={`contrib-media-item-${m.id}`}>
      <div className="aspect-video bg-slate-900 flex items-center justify-center">
        {m.type === "image" ? (
          <img src={`${API}/public/contribution-media/${contributionId}/${m.id}`} alt="" className="w-full h-full object-cover pointer-events-none" loading="lazy" />
        ) : (
          <video src={`${API}/public/contribution-media/${contributionId}/${m.id}`} className="w-full h-full object-cover" controls preload="metadata" />
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <div className="bg-violet-600 text-white text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center shadow" title="ترتيب">
            {index + 1}
          </div>
          <div className="bg-black/70 text-white text-[10px] font-black px-2 py-0.5 rounded-full backdrop-blur">
            {m.type === "image" ? "🖼️ صورة" : "🎬 فيديو"}
          </div>
          {isCover && (
            <div className="bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow flex items-center gap-1" title="الغلاف المثبّت">
              📌 غلاف
            </div>
          )}
        </div>
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          <button
            onClick={onDelete}
            className="w-8 h-8 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center shadow-lg"
            title="حذف"
            data-testid={`contrib-media-delete-${m.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={isCover ? onUnpinCover : onPinCover}
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg text-white ${isCover ? "bg-slate-600 hover:bg-slate-700" : "bg-amber-500 hover:bg-amber-600"}`}
            title={isCover ? "إلغاء تثبيت الغلاف" : "تثبيت كغلاف للبطاقة"}
            data-testid={`contrib-media-pin-${m.id}`}
          >
            {isCover ? "📍" : "📌"}
          </button>
          {m.type === "video" && (
            <button
              onClick={() => setPosterOpen(true)}
              className="w-8 h-8 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white flex items-center justify-center shadow-lg"
              title="اختيار غلاف الفيديو (Poster)"
              data-testid={`contrib-media-poster-${m.id}`}
            >
              🎬
            </button>
          )}
        </div>
        <div className="absolute bottom-2 left-2 flex flex-col gap-1 opacity-90">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="w-7 h-7 rounded-md bg-white/95 hover:bg-white text-slate-800 flex items-center justify-center shadow disabled:opacity-30 disabled:cursor-not-allowed"
            title="نقل للأمام"
            data-testid={`contrib-media-up-${m.id}`}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="w-7 h-7 rounded-md bg-white/95 hover:bg-white text-slate-800 flex items-center justify-center shadow disabled:opacity-30 disabled:cursor-not-allowed"
            title="نقل للخلف"
            data-testid={`contrib-media-down-${m.id}`}
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>
        <div className="absolute bottom-2 right-2 bg-white/95 rounded-md p-1 shadow" title="اسحب لإعادة الترتيب">
          <GripVertical className="w-4 h-4 text-slate-500" />
        </div>
      </div>
      <div className="p-2 bg-white">
        <div className="flex items-start gap-1.5">
          <Textarea
            value={caption}
            onChange={(e) => { setCaption(e.target.value); setDirty(true); }}
            placeholder="اكتب تعليقاً قصيراً يظهر أسفل الصورة..."
            rows={2}
            maxLength={280}
            className="text-xs"
            data-testid={`contrib-media-caption-${m.id}`}
          />
          {dirty && (
            <Button size="sm" onClick={save} disabled={saving} className="shrink-0 bg-violet-600 hover:bg-violet-700 text-white px-2" data-testid={`contrib-media-save-${m.id}`}>
              {saving ? "..." : "حفظ"}
            </Button>
          )}
        </div>
        <div className="text-[10px] text-slate-400 mt-1 text-left">{caption.length}/280</div>
      </div>
      {posterOpen && (
        <PosterPickerDialog
          media={m}
          contributionId={contributionId}
          onClose={() => setPosterOpen(false)}
          onSaved={() => { setPosterOpen(false); setPosterVersion((v) => v + 1); }}
        />
      )}
    </div>
  );
}


function PosterPickerDialog({ media: m, contributionId, onClose, onSaved }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [preview, setPreview] = useState(null); // dataURL
  const [saving, setSaving] = useState(false);
  const videoUrl = `${API}/public/contribution-media/${contributionId}/${m.id}`;

  const captureFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w;
    c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    setPreview(c.toDataURL("image/jpeg", 0.9));
  };

  const savePoster = async () => {
    if (!preview) { toast.error("التقط لقطة من الفيديو أولاً"); return; }
    setSaving(true);
    try {
      const blob = await (await fetch(preview)).blob();
      const fd = new FormData();
      fd.append("file", blob, "poster.jpg");
      await api.post(`/admin/director-contributions/${contributionId}/media/${m.id}/poster`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("تم حفظ الغلاف");
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="poster-picker-dialog">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} dir="rtl">
        <div className="sticky top-0 bg-gradient-to-l from-fuchsia-600 to-violet-600 text-white px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <div className="text-xs font-black opacity-90">🎬 اختر غلاف الفيديو</div>
            <div className="font-black text-sm mt-1">شغّل الفيديو، اذهب للمشهد المناسب، ثم اضغط "التقط لقطة"</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="poster-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-2xl overflow-hidden bg-black">
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              crossOrigin="anonymous"
              className="w-full aspect-video bg-black"
              data-testid="poster-picker-video"
            />
          </div>
          <div className="flex items-center gap-2 justify-center">
            <Button onClick={captureFrame} className="bg-violet-600 hover:bg-violet-700 text-white gap-2" data-testid="poster-picker-capture">
              📸 التقط لقطة من هذا المشهد
            </Button>
            {preview && (
              <Button onClick={() => setPreview(null)} variant="outline" data-testid="poster-picker-reset">
                إعادة التعيين
              </Button>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />
          {preview && (
            <div className="rounded-2xl border-2 border-fuchsia-200 bg-fuchsia-50/50 p-3">
              <div className="text-xs font-black text-fuchsia-800 mb-2">معاينة الغلاف المختار:</div>
              <img src={preview} alt="poster preview" className="w-full rounded-xl shadow-lg" data-testid="poster-picker-preview" />
              <Button onClick={savePoster} disabled={saving} className="w-full mt-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white gap-2" data-testid="poster-picker-save">
                <Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ..." : "حفظ هذا الغلاف"}
              </Button>
            </div>
          )}
          {!preview && (
            <div className="text-center text-xs text-slate-500 bg-slate-50 rounded-xl p-4">
              💡 نصيحة: اختر لقطة واضحة يظهر فيها المدير أو العنوان الرئيسي للندوة — سيتم استخدامها كصورة ثابتة قبل تشغيل الفيديو.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
